import React, { useEffect, useState } from "react";

import { useDocTitle } from "../../utils/hooks";
import WithMainLayout from "../../hocs/layouts/MainLayout";
import { useAPIs } from "../../apis/APIsConfig";
import {
  getBeneficios,
  getRequisitos,
  ICONS,
  parseValidationMessages,
  validateRequest,
} from "../../services/benefits";
import { AddBenefit } from "./AddBenefit";
import BenefitValidationAlert from "./BenefitValidationAlert";
import { useBeneficiosAplicarIds } from "../../utils/beneficiosAplicar";

interface ValidationAlertState {
  show: boolean;
  messages: string[];
  // Si está definido, al cerrar el modal (advertencia no bloqueante) se continúa
  // abriendo el wizard para este beneficio. Si es undefined, el modal es bloqueante.
  continueBenefit?: any;
}

function Benefits() {
  useAPIs();
  useDocTitle("Beneficios");
  const aplicarIds = useBeneficiosAplicarIds();
  const [benefits, setBenefits] = useState<any[]>([]);
  const [activeBenefit, setActiveBenefit] = useState<any>(undefined);
  const [soloInformacion, setSoloInformacion] = useState(false);
  const [validationAlert, setValidationAlert] = useState<ValidationAlertState>({
    show: false,
    messages: [],
  });

  useEffect(() => {
    getBeneficios().then((res) => {
      setBenefits(res.data.value);
    });
  }, []);

  function abrirWizard(benefit: any) {
    getRequisitos(benefit.codBeneficio).then((resRequisitos) => {
      setSoloInformacion(false);
      setActiveBenefit({
        ...benefit,
        requisitos: resRequisitos.data.value,
      });
    });
  }

  function closeValidationAlert() {
    // Las advertencias no bloqueantes continúan al wizard una vez aceptadas.
    const continueBenefit = validationAlert.continueBenefit;
    setValidationAlert({ show: false, messages: [] });
    if (continueBenefit) {
      abrirWizard(continueBenefit);
    }
  }

  function selectBenefit(benefit: any) {
    validateRequest(benefit.codBeneficio).then((res) => {
      if (res && res.data.value) {
        // Sin errores bloqueantes. Si el backend devolvió advertencias (Code 100,
        // p. ej. Ingreso Tardío en CEA), mostrarlas y continuar al cerrar.
        const advertencias = parseValidationMessages(res);
        if (advertencias.length > 0) {
          setValidationAlert({
            show: true,
            messages: advertencias,
            continueBenefit: benefit,
          });
        } else {
          abrirWizard(benefit);
        }
      } else {
        // Errores bloqueantes (Code 200): mostrar el modal y no abrir el wizard.
        const messages = res
          ? parseValidationMessages(res)
          : [
              "No cumples con los requisitos para solicitar este beneficio (pensión por vejez).",
            ];

        if (messages.length > 0) {
          setValidationAlert({ show: true, messages });
        }
      }
    });
  }

  // "Ver Información": solo lectura, sin validar ni iniciar una solicitud.
  function verInformacion(benefit: any) {
    getRequisitos(benefit.codBeneficio).then((resRequisitos) => {
      setSoloInformacion(true);
      setActiveBenefit({
        ...benefit,
        requisitos: resRequisitos.data.value,
      });
    });
  }

  function cerrarBenefit() {
    setActiveBenefit(undefined);
    setSoloInformacion(false);
  }

  return (
    <>
      <BenefitValidationAlert
        show={validationAlert.show}
        messages={validationAlert.messages}
        onDismiss={closeValidationAlert}
      />
      {activeBenefit === undefined && (
        <div className="block pt-10 lg:pt-16 pb-20 md:pb-[340px]">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-x-6 xxl:gap-x-8 gap-y-4 md:gap-y-12">
            {benefits.map((benefit) => {
              const puedeAplicar = aplicarIds.has(Number(benefit.codBeneficio));
              return (
                <a
                  key={benefit.codBeneficio}
                  onClick={() =>
                    puedeAplicar
                      ? selectBenefit(benefit)
                      : verInformacion(benefit)
                  }
                  className="benefit-card"
                >
                  <div className="description">
                    <div className="icon-container">
                      <i
                        className={`icon icon-${ICONS[benefit.codBeneficio]}`}
                      ></i>
                    </div>
                    <h5>{benefit.nombre}</h5>
                  </div>
                  <span className="btn btn-secondary btn-medium hidden md:inline-flex">
                    {puedeAplicar ? "Aplicar" : "Ver Información"}
                    <i className="icon icon-arrow-right-short left"></i>
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      )}
      {activeBenefit !== undefined && (
        <AddBenefit
          benefit={activeBenefit}
          soloInformacion={soloInformacion}
          cancel={cerrarBenefit}
        />
      )}
    </>
  );
}

export default WithMainLayout(Benefits, "Beneficios");
